import { MagnifyingGlass, XCircle } from '@phosphor-icons/react';
import { format as formatDate } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'src/components/Button';
import { CallDetailsModal } from 'src/components/CallDetailsModal';
import { ContentWrapper } from 'src/components/ContentWrapper';
import { CopyableField } from 'src/components/CopyableField';
import { Info } from 'src/components/Info';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Modal } from 'src/components/Modal';
import { SelectBox } from 'src/components/SelectBox';
import {
  SideDropAction,
  SideDropActions,
} from 'src/components/SideDropActions';
import Table, { Header, Row } from 'src/components/Table';
import { useEnvironment } from 'src/contexts/EnvironmentContext';
import { useNotification } from 'src/contexts/NotificationContext';
import useAuthedRequest from 'src/hooks/useAuthedRequest';
import { Can, can, Subject } from 'src/hooks/usePermissions';
import { Call } from 'src/interfaces/call.interface';
import {
  FluentsActionKey,
  PaginatedResponse,
  WorkflowActionProvider,
  WorkflowExecutionCancelationReason,
  WorkflowExecutionStatus,
  WorkflowExecutionThreadActionHistoryRecord,
  WorkflowExecutionThreadFindAllFilters,
  WorkflowExecutionThreadStatus,
  WorkflowExecutionThreadWithComputedStatus,
  WorkflowWithStatus,
} from 'src/interfaces/workflows.interface';
import { friendlyDateFormatter } from 'src/utils/date';
import { processPhoneNumber, extractDigits } from 'src/utils/number';
import { ActivityList, ActivityItemData } from './ui/ActivityList';
import { QueryObject } from 'src/interfaces/queryObject.interface';
import { Option } from 'src/components/SelectBox';
import { AutoCompleteSelectBoxControlled } from 'src/components/AutoCompleteSelectBoxControlled';

// TODO: use the campaigns interface so we don't have to hardcode this
const TO_NUMBER_PARAM_KEY = 'to_number';

const DEFAULT_CAMPAIGN_FILTER_OPTION: Option = {
  label: 'All Campaigns',
  value: '',
};

const ALL_THREAD_STATUSES_OPTION: Option = {
  label: 'All Statuses',
  value: '',
};

const THREAD_STATUS_OPTIONS: Option[] = [
  ALL_THREAD_STATUSES_OPTION,
  { label: 'Scheduled', value: WorkflowExecutionThreadStatus.RUNNING },
  {
    label: 'Queued',
    value: WorkflowExecutionThreadStatus.RATE_LIMITED,
  },
  {
    label: 'Inactive',
    value: WorkflowExecutionThreadStatus.PAUSED,
  },
  { label: 'Canceled', value: WorkflowExecutionThreadStatus.CANCELED },
  { label: 'Errored', value: WorkflowExecutionThreadStatus.ERRORED },
  { label: 'Completed', value: WorkflowExecutionThreadStatus.COMPLETED },
];

type MonitoringTabFilters = {
  reference_id?: string;
  status?: string;
  to_number?: string;
};

export const AutomationsHistoryTab = () => {
  const { role } = useEnvironment();
  const { get, post } = useAuthedRequest();
  const notification = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [sortConfig, setSortConfig] = useState<{
    key: string | null;
    direction: string | null;
  }>({ key: null, direction: null });
  const [currentPage, setCurrentPage] = useState(1);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [selectedThread, setSelectedThread] =
    useState<WorkflowExecutionThreadWithComputedStatus | null>(null);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedCall, setSelectedCall] = useState<Call | null>(null);
  const [campaignOptions, setCampaignOptions] = useState<Option[]>([]);
  const [
    firstCampaignOptionsFetchCompleted,
    setFirstCampaignOptionsFetchCompleted,
  ] = useState(false);
  const [campaignFilterFetched, setCampaignFilterFetched] = useState(false);

  // Initialize filters from URL parameters and track if there was an initial reference_id
  const [initialReferenceId] = useState(() => {
    return searchParams.get('reference_id') || null;
  });

  const [filters, setFilters] = useState<MonitoringTabFilters>(() => {
    const reference_id =
      searchParams.get('reference_id') || DEFAULT_CAMPAIGN_FILTER_OPTION.value;
    const status =
      searchParams.get('status') || ALL_THREAD_STATUSES_OPTION.value;
    const to_number = searchParams.get('to_number') || '';

    return {
      reference_id,
      status,
      to_number,
    };
  });

  const headers: Header[] = [
    { key: 'id', label: 'ID', width: '8rem', disableSorting: true },
    { key: 'updated_at', label: 'Updated At', width: '10%' },
    { key: 'workflow_label', label: 'Campaign', width: '25%' },
    { key: 'toNumber', label: 'To', width: '10%', disableSorting: true },
    { key: 'status', label: 'Status', width: '10%', disableSorting: true },
    { key: 'activity', label: 'Activity', width: '25%', disableSorting: true },
    {
      key: 'action',
      label: '',
      width: '10%',
      disableSorting: true,
      align: 'center',
    },
  ];

  const updateFilters = (newFilters: Partial<typeof filters>) => {
    setFilters((prevFilters) => {
      const updatedFilters = { ...prevFilters, ...newFilters };
      setCurrentPage(1);

      // Update URL parameters
      const newSearchParams = new URLSearchParams(searchParams);

      // Update or remove reference_id
      if (
        updatedFilters.reference_id !== DEFAULT_CAMPAIGN_FILTER_OPTION.value
      ) {
        newSearchParams.set('reference_id', updatedFilters.reference_id || '');
      } else {
        newSearchParams.delete('reference_id');
      }

      // Update or remove status
      if (updatedFilters.status !== ALL_THREAD_STATUSES_OPTION.value) {
        newSearchParams.set('status', updatedFilters.status || '');
      } else {
        newSearchParams.delete('status');
      }

      // Update or remove to_number
      if (updatedFilters.to_number) {
        newSearchParams.set('to_number', updatedFilters.to_number);
      } else {
        newSearchParams.delete('to_number');
      }

      setSearchParams(newSearchParams, { replace: true });

      return updatedFilters;
    });
  };

  const getActionKeyDescription = (
    actionHistory: WorkflowExecutionThreadActionHistoryRecord[],
  ) => {
    const items: ActivityItemData[] = actionHistory
      .map((history, i) => {
        let label = '';
        let icon = undefined;
        let onActivityClick = undefined;
        switch (history.action_config.action_key) {
          case 'create_call':
            label = i === 0 ? 'Initial Call' : 'Follow-up Call';
            icon = MagnifyingGlass;
            onActivityClick = () => selectActionHistory(history);
            break;
          case 'send_email':
            label = 'Follow-up Email';
            break;
          case 'send_message':
            label = 'Follow-up SMS';
            break;
          default:
            return null;
        }
        return {
          completedAt: history.completed_at || '',
          label,
          icon,
          onClick: onActivityClick,
        } as ActivityItemData;
      })
      .filter((item): item is ActivityItemData => item !== null);
    return <ActivityList items={items} />;
  };

  const fetchCampaignOptions = useCallback(
    async (name: string): Promise<void> => {
      try {
        const nameTrimmed = name.trim();
        const searchResultsSize = 5;
        const queryObject: QueryObject = {
          sort_column: 'label',
          sort_desc: 'false',
          size: String(searchResultsSize),
          ...(nameTrimmed.length !== 0 && {
            filters: JSON.stringify({ label: nameTrimmed }),
          }),
        };
        const query = new URLSearchParams(
          queryObject as Record<string, string>,
        );

        const data = await get(`/workflows?${query.toString()}`);

        const options = data.items.map((workflow: WorkflowWithStatus) => ({
          value: workflow.referenceId,
          label: workflow.label,
        }));

        setCampaignOptions(options);

        // Mark first fetch as completed
        if (!firstCampaignOptionsFetchCompleted) {
          setFirstCampaignOptionsFetchCompleted(true);
        }
      } catch (error) {
        console.error('Error fetching campaign options:', error);
      }
    },
    [get, firstCampaignOptionsFetchCompleted],
  );

  const fetchCampaignFilter = useCallback(
    async (referenceId: string) => {
      try {
        const workflow = await get(`/workflows/${referenceId}`);
        if (workflow) {
          const campaignOption = {
            value: workflow.referenceId,
            label: workflow.label,
          };

          // Only add the campaign option if it's not already in the current options
          setCampaignOptions((prevOptions) => {
            const existingOption = prevOptions.find(
              (opt) => opt.value === referenceId,
            );
            if (!existingOption) {
              return [campaignOption, ...prevOptions];
            }
            return prevOptions;
          });
        }
      } catch (error) {
        console.error('Error loading campaign by reference ID:', error);
      }
    },
    [get],
  );

  const filterByCampaign = (workflow_reference_id: string) => {
    updateFilters({ reference_id: workflow_reference_id });
  };

  const filterByStatus = (status: string) => {
    updateFilters({ status });
  };

  const fetchThreads = async (page: number, pageSize: number) => {
    // TODO: Handle canceling outstanding fetch requests.
    if (loading) return;

    setLoading(true);
    try {
      const queryFilters: WorkflowExecutionThreadFindAllFilters = {};
      if (filters.reference_id !== DEFAULT_CAMPAIGN_FILTER_OPTION.value) {
        queryFilters.workflowReferenceId = filters.reference_id;
      }
      if (filters.status !== ALL_THREAD_STATUSES_OPTION.value) {
        queryFilters.statuses = [
          filters.status as WorkflowExecutionThreadStatus,
        ];
      }
      const numberDigitsOnly = extractDigits(filters.to_number || '');
      if (numberDigitsOnly.length > 0) {
        queryFilters.executionParams = {
          [TO_NUMBER_PARAM_KEY]: numberDigitsOnly,
        };
      }

      const queryObject: QueryObject = {
        page: String(page),
        size: String(pageSize),
        filters: JSON.stringify(queryFilters),
      };

      if (sortConfig.key) {
        queryObject.sort_column = sortConfig.key;
        queryObject.sort_desc =
          sortConfig.direction === 'desc' ? 'true' : 'false';
      }

      const query = new URLSearchParams(queryObject as Record<string, string>);

      const data: PaginatedResponse<WorkflowExecutionThreadWithComputedStatus> =
        await get(`/workflow_threads?${query.toString()}`);
      // Map each thread into a row for the table.
      const items = data.items.map(
        (thread: WorkflowExecutionThreadWithComputedStatus) => {
          let status = '';
          let cancelable = false;

          if (thread.status === WorkflowExecutionThreadStatus.CANCELED) {
            status = 'Canceled';
            if (
              thread.execution_status ===
                WorkflowExecutionStatus.EXECUTION_CANCELED &&
              (thread.execution_cancelation_reason ===
                WorkflowExecutionCancelationReason.REASON_CALL_ANSWERED ||
                thread.execution_cancelation_reason ===
                  WorkflowExecutionCancelationReason.REASON_INCOMING_CALL)
            ) {
              status = 'Stopped Follow-ups — Contact Reached';
            }
          } else if (thread.status === WorkflowExecutionThreadStatus.ERRORED) {
            status = 'Errored';
          } else if (thread.status === WorkflowExecutionThreadStatus.PAUSED) {
            status = 'Inactive';
            cancelable = true;
          } else if (
            thread.status === WorkflowExecutionThreadStatus.RUNNING ||
            thread.status === WorkflowExecutionThreadStatus.RATE_LIMITED
          ) {
            status = 'Scheduled';
            cancelable = true;
            if (thread.status === WorkflowExecutionThreadStatus.RATE_LIMITED) {
              status = 'Queued — Max Concurrent Calls';
            } else if (thread.resume_at) {
              if (
                thread.pending_action &&
                !thread.pending_action.result &&
                !thread.pending_action.error
              ) {
                status = 'Outreach In Progress';
                if (
                  thread.pending_action?.action_provider ===
                    WorkflowActionProvider.FLUENTS &&
                  thread.pending_action?.action_config?.action_key ===
                    FluentsActionKey.CREATE_CALL
                ) {
                  status = 'Calling';
                }
              } else {
                const executionNextResumeAtDate = new Date(thread.resume_at);

                if (executionNextResumeAtDate > new Date()) {
                  const nextResumeAtStr = formatDate(
                    executionNextResumeAtDate,
                    'MM/dd/yyyy HH:mm:ss',
                  );
                  status += ` for ${nextResumeAtStr}`;
                }
              }
            }
          } else {
            status = 'Completed';
          }

          const actions: SideDropAction[] = [
            {
              label: 'Cancel',
              Icon: XCircle,
              onClick: () => openCancelModal(thread),
              disabled: !cancelable,
              show: can(role, 'update', Subject.CAMPAIGNS),
            },
          ];

          const activity =
            thread.action_history.length > 0
              ? getActionKeyDescription(thread.action_history)
              : '—';

          const lastActionHistoryRecord =
            thread.action_history.length > 0
              ? thread.action_history[thread.action_history.length - 1]
              : undefined;
          const lastErrorMessage = lastActionHistoryRecord?.error?.trim();

          const statusDisplay =
            thread.status === WorkflowExecutionThreadStatus.ERRORED &&
            lastErrorMessage ? (
              <div className="flex items-center gap-2">
                <span>Errored</span>
                <Info value={lastErrorMessage} />
              </div>
            ) : (
              status
            );

          return {
            id: <CopyableField value={thread.id} notification={notification} />,
            updated_at: friendlyDateFormatter(new Date(thread.updated_at)),
            workflow_label: thread.workflow_label,
            workflow_reference_id: thread.workflow_reference_id,
            toNumber: processPhoneNumber(
              (thread.execution_params?.to_number as string) || '',
            ),
            status: statusDisplay,
            action: (
              <div
                className="flex justify-center items-center"
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <SideDropActions options={actions} />
              </div>
            ),
            activity,
          };
        },
      );
      setRows(items);
      setTotalItems(data.total);
    } catch (error) {
      console.error('Error fetching threads:', error);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!loading) {
      fetchThreads(currentPage, currentPageSize);
    }
  }, [currentPage, currentPageSize, sortConfig, filters]);

  // Load campaign option when component mounts with reference_id from URL
  useEffect(() => {
    if (
      firstCampaignOptionsFetchCompleted &&
      !campaignFilterFetched &&
      initialReferenceId &&
      initialReferenceId !== DEFAULT_CAMPAIGN_FILTER_OPTION.value
    ) {
      fetchCampaignFilter(initialReferenceId);
      setCampaignFilterFetched(true);
    }
  }, [
    fetchCampaignFilter,
    initialReferenceId,
    firstCampaignOptionsFetchCompleted,
    campaignFilterFetched,
  ]);

  const handlePageChange = (page: number, perPage: number) => {
    setCurrentPage(page);
    setCurrentPageSize(perPage);
  };

  const handleSizeChange = (perPage: number) => {
    setCurrentPageSize(perPage);
    setCurrentPage(1);
  };

  const handleSort = (key: string, direction: string) => {
    setSortConfig({ key, direction });
  };

  const openCancelModal = (
    thread: WorkflowExecutionThreadWithComputedStatus,
  ) => {
    setSelectedThread(thread);
    setCancelModalOpen(true);
  };

  const handleCancelCall = async () => {
    if (!selectedThread) return;
    try {
      // TODO: We should move this to a thread cancelation endpoint.
      await post(
        `/workflow_executions/${selectedThread.execution_id}/cancel`,
        {},
      );
      notification.success('Successfully canceled scheduled call');
      fetchThreads(currentPage, currentPageSize);
      setCancelModalOpen(false);
    } catch (error) {
      console.error('Error canceling scheduled call:', error);
      notification.error('Failed to cancel scheduled call');
    }
  };
  const selectActionHistory = async (
    actionHistory: WorkflowExecutionThreadActionHistoryRecord,
  ) => {
    if (
      actionHistory.action_provider !== WorkflowActionProvider.FLUENTS ||
      actionHistory.action_config.action_key !== FluentsActionKey.CREATE_CALL
    ) {
      return;
    }

    if (!actionHistory.result || !actionHistory.result.id) return;

    setLoading(true);

    const call = await getCallDetails(actionHistory.result.id as string);
    setSelectedCall(call);

    setLoading(false);
  };

  const getCallDetails = async (callId: string) => {
    const call = await get(`/calls/${callId}`);
    return call;
  };

  return (
    <ContentWrapper>
      <Can operation="read" subject={Subject.CAMPAIGNS}>
        <div className="mb-6">
          <div className="flex flex-col gap-4 sm:gap-2 sm:grid-cols-3 md:grid md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 mt-4">
            <div>
              <Label htmlFor="campaigns">Campaign</Label>

              <AutoCompleteSelectBoxControlled
                value={
                  campaignOptions.find(
                    (option) => option.value === filters.reference_id,
                  ) || DEFAULT_CAMPAIGN_FILTER_OPTION
                }
                fetchOptions={fetchCampaignOptions}
                options={campaignOptions}
                onChange={(value) => filterByCampaign(value)}
                variant="outlined"
                color="primary"
                size="medium"
                disabled={loading}
              />
            </div>

            <div>
              <Label htmlFor="status">Status</Label>

              <SelectBox
                options={THREAD_STATUS_OPTIONS}
                defaultValue={THREAD_STATUS_OPTIONS.find(
                  (option) => option.value === filters.status,
                )}
                variant="outlined"
                color="primary"
                size="medium"
                className="col-span-2"
                onChange={(value) => filterByStatus(value)}
              />
            </div>

            <div>
              <Input
                label="To Number"
                type="text"
                value={filters.to_number}
                onChange={(e) => updateFilters({ to_number: e.target.value })}
              />
            </div>
          </div>
        </div>

        <Table
          headers={headers}
          rows={rows}
          totalItems={totalItems}
          currentPage={currentPage}
          onPageChange={handlePageChange}
          onSort={handleSort}
          loading={loading}
          setSize={handleSizeChange}
          defaultSize={currentPageSize}
        />
      </Can>

      {/* Cancel Scheduled Call Modal */}
      <Modal
        title={`Cancel Scheduled Call`}
        isOpen={cancelModalOpen}
        onClose={() => {
          setCancelModalOpen(false);
          setSelectedThread(null);
        }}
        actionButton={
          <Button onClick={handleCancelCall}>Cancel Scheduled Call</Button>
        }
        className="w-auto max-w-md"
      >
        <div className="text-center">
          Are you sure you want to cancel this scheduled call?
        </div>
      </Modal>

      {selectedCall && (
        <CallDetailsModal
          call={selectedCall}
          isOpen={!!selectedCall}
          onClose={() => {
            setSelectedCall(null);
          }}
        />
      )}
    </ContentWrapper>
  );
};
